import Phaser from "phaser";
import { getAgritechLanguage, getAgritechText } from "../utils/agritechText";

export default class LeaderboardScene extends Phaser.Scene {
  constructor() {
    super("LeaderboardScene");
  }

  create() {
    const t = getAgritechText();
    const isSpanish = getAgritechLanguage() === "es";
    const isTouchDevice = window.matchMedia(
      "(any-pointer: coarse), (hover: none)",
    ).matches;

    const labels = {
      adults: isSpanish ? "ADULTOS" : "ADULTS",
      minors: isSpanish ? "MENORES" : "UNDER 18",
      initials: isSpanish ? "INICIALES" : "INITIALS",
      playerId: isSpanish ? "ID DE JUGADOR" : "PLAYER ID",
      score: isSpanish ? "PUNTAJE" : "SCORE",
      loading: isSpanish ? "Cargando..." : "Loading...",
      empty: isSpanish
        ? "Todavía no hay puntajes en esta categoría."
        : "There are no scores in this category yet.",
      scroll: isTouchDevice
        ? isSpanish
          ? "Desliza o arrastra la barra para desplazarte"
          : "Swipe or drag the scroll bar to scroll"
        : isSpanish
          ? "Usa ↑ / ↓, la rueda o arrastra la barra"
          : "Use ↑ / ↓, the mouse wheel, or drag the scroll bar",
      download: isSpanish ? "DESCARGAR" : "DOWNLOAD",
    };

    const maskMinorParticipantId = (value) => {
      const participantId = String(value ?? "").trim();

      if (!participantId) return "------";
      if (!participantId.includes("@")) return participantId;

      const atIndex = participantId.lastIndexOf("@");
      const localPart = participantId.slice(0, atIndex);
      const domain = participantId.slice(atIndex + 1);

      if (!localPart || !domain) return "------";

      // Normal case: first 3 + masked middle + last 3 + full domain.
      if (localPart.length >= 7) {
        return `${localPart.slice(0, 3)}***${localPart.slice(-3)}@${domain}`;
      }

      // Short local parts need extra masking so the full name/identifier
      // is never exposed simply because there are 6 or fewer characters.
      if (localPart.length >= 4) {
        return `${localPart.slice(0, 2)}***${localPart.slice(-2)}@${domain}`;
      }

      return `${localPart.slice(0, 1)}***@${domain}`;
    };

    this.rowItems = [];
    this.leaderboardRows = [];
    this.leaderboardStartIndex = 0;
    this.maxVisibleRows = 10;
    this.activeGroup = "adult";

    this.add.image(400, 300, "titleBG").setDisplaySize(800, 600);
    this.add.rectangle(400, 300, 800, 600, 0x06130c, 0.7).setDepth(1);

    this.add
      .rectangle(400, 300, 700, 490, 0x18231d, 0.98)
      .setStrokeStyle(3, 0x769b55)
      .setDepth(2);

    this.add
      .text(400, 82, t.leaderboard, {
        fontFamily: '"Russo One", sans-serif',
        fontSize: "36px",
        color: "#ecfccb",
        stroke: "#14532d",
        strokeThickness: 3,
        shadow: {
          offsetX: 0,
          offsetY: 3,
          color: "#020d07",
          blur: 6,
          fill: true,
        },
      })
      .setOrigin(0.5)
      .setDepth(3);

    const downloadButton = this.add
      .rectangle(650, 84, 142, 34, 0x0b1510, 0.98)
      .setStrokeStyle(2, 0x6f8f5b)
      .setDepth(3)
      .setInteractive({ useHandCursor: true });

    const downloadText = this.add
      .text(650, 84, labels.download, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "13px",
        color: "#f8fafc",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    downloadButton.on("pointerover", () => {
      this.sound.play("tap", { volume: 0.45 });
      downloadButton.setFillStyle(0x26382b, 1);
      downloadText.setColor("#d9f99d");
    });

    downloadButton.on("pointerout", () => {
      downloadButton.setFillStyle(0x0b1510, 0.98);
      downloadText.setColor("#f8fafc");
    });

    downloadButton.on("pointerdown", () => {
      this.sound.play("selected", { volume: 0.5 });
      window.dispatchEvent(new Event("biorev-download-excel"));
    });

    const createTab = (x, label, group) => {
      const button = this.add
        .rectangle(x, 134, 245, 42, 0x0b1510, 1)
        .setStrokeStyle(2, 0x405747)
        .setDepth(3)
        .setInteractive({ useHandCursor: true });

      const text = this.add
        .text(x, 134, label, {
          fontFamily: '"Montserrat", sans-serif',
          fontSize: "16px",
          color: "#cbd5c0",
          fontStyle: "bold",
        })
        .setOrigin(0.5)
        .setDepth(4);

      button.on("pointerover", () => {
        if (this.activeGroup === group) return;
        this.sound.play("tap", { volume: 0.4 });
        button.setFillStyle(0x26382b, 1);
        button.setStrokeStyle(2, 0x769b55);
        text.setColor("#d9f99d");
      });

      button.on("pointerout", () => this.updateTabs());

      button.on("pointerdown", () => {
        if (this.activeGroup === group) return;
        this.sound.play("selected", { volume: 0.5 });
        this.activeGroup = group;
        this.leaderboardStartIndex = 0;
        this.updateTabs();
        this.renderRows();
      });

      return { button, text, group };
    };

    this.tabs = [
      createTab(270, labels.adults, "adult"),
      createTab(530, labels.minors, "minor"),
    ];

    this.updateTabs = () => {
      this.tabs.forEach(({ button, text, group }) => {
        const selected = this.activeGroup === group;
        button.setFillStyle(selected ? 0x65a30d : 0x0b1510, 1);
        button.setStrokeStyle(2, selected ? 0xd9f99d : 0x405747);
        text.setColor(selected ? "#ffffff" : "#cbd5c0");
      });
    };

    this.updateTabs();

    this.headerBackground = this.add
      .rectangle(400, 178, 620, 34, 0x0b1510, 1)
      .setStrokeStyle(1, 0x769b55)
      .setDepth(3);

    this.rankHeader = this.add
      .text(122, 178, "#", {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "15px",
        color: "#a3e635",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    this.identityHeader = this.add
      .text(183, 178, labels.initials, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "15px",
        color: "#d9f99d",
        fontStyle: "bold",
      })
      .setOrigin(0, 0.5)
      .setDepth(4);

    this.scoreHeader = this.add
      .text(645, 178, labels.score, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "15px",
        color: "#d9f99d",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    this.add.rectangle(158, 322, 1, 288, 0x405747, 1).setDepth(4);
    this.add.rectangle(574, 322, 1, 288, 0x405747, 1).setDepth(4);

    this.loadingText = this.add
      .text(400, 325, labels.loading, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "21px",
        color: "#f7faf4",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(5);

    const clearRows = () => {
      this.rowItems.forEach((item) => item.destroy());
      this.rowItems = [];
    };

    const getActiveRows = () =>
      this.leaderboardRows.filter((row) =>
        this.activeGroup === "adult" ? row.isAdult === true : !row.isAdult,
      );

    const scrollbarTop = 197;
    const scrollbarBottom = 463;
    const scrollbarHeight = scrollbarBottom - scrollbarTop;
    const scrollbarX = 726;
    const scrollbarTrackWidth = isTouchDevice ? 14 : 10;
    const scrollbarThumbWidth = isTouchDevice ? 22 : 16;
    const scrollbarThumbHeight = isTouchDevice ? 58 : 48;

    this.isDraggingLeaderboardScrollbar = false;

    this.scrollbarTrack = this.add
      .rectangle(
        scrollbarX,
        scrollbarTop + scrollbarHeight / 2,
        scrollbarTrackWidth,
        scrollbarHeight,
        0x0b1510,
        0.9,
      )
      .setStrokeStyle(1, 0x405747, 0.9)
      .setDepth(4)
      .setInteractive({ useHandCursor: true });

    this.scrollbarThumb = this.add
      .rectangle(
        scrollbarX,
        scrollbarTop + scrollbarThumbHeight / 2,
        scrollbarThumbWidth,
        scrollbarThumbHeight,
        0x769b55,
        1,
      )
      .setStrokeStyle(1, 0xd9f99d, 1)
      .setDepth(6)
      .setInteractive({ useHandCursor: true });

    this.input.setDraggable(this.scrollbarThumb);
    this.scrollbarTrack.setVisible(false);
    this.scrollbarThumb.setVisible(false);
    this.scrollbarTrack.input.enabled = false;
    this.scrollbarThumb.input.enabled = false;

    const updateScrollbar = () => {
      const rows = getActiveRows();
      const canScroll = rows.length > this.maxVisibleRows;

      this.scrollbarTrack.setVisible(canScroll);
      this.scrollbarThumb.setVisible(canScroll);
      this.scrollbarTrack.input.enabled = canScroll;
      this.scrollbarThumb.input.enabled = canScroll;

      if (!canScroll) return;

      const maxStartIndex = Math.max(0, rows.length - this.maxVisibleRows);
      const travel = scrollbarHeight - scrollbarThumbHeight;
      const progress =
        maxStartIndex === 0 ? 0 : this.leaderboardStartIndex / maxStartIndex;

      this.scrollbarThumb.y =
        scrollbarTop + scrollbarThumbHeight / 2 + travel * progress;
    };

    const setScrollFromThumbY = (thumbCenterY) => {
      const rows = getActiveRows();
      if (rows.length <= this.maxVisibleRows) return;

      const maxStartIndex = Math.max(0, rows.length - this.maxVisibleRows);
      const travel = scrollbarHeight - scrollbarThumbHeight;
      const minY = scrollbarTop + scrollbarThumbHeight / 2;
      const maxY = scrollbarBottom - scrollbarThumbHeight / 2;
      const clampedY = Phaser.Math.Clamp(thumbCenterY, minY, maxY);
      const progress = travel <= 0 ? 0 : (clampedY - minY) / travel;
      const nextIndex = Phaser.Math.Clamp(
        Math.round(progress * maxStartIndex),
        0,
        maxStartIndex,
      );

      this.scrollbarThumb.y = clampedY;
      if (nextIndex === this.leaderboardStartIndex) return;

      this.leaderboardStartIndex = nextIndex;
      this.renderRows();
    };

    this.renderRows = () => {
      clearRows();

      const rows = getActiveRows();
      this.identityHeader.setText(
        this.activeGroup === "adult" ? labels.initials : labels.playerId,
      );

      if (rows.length === 0) {
        const emptyText = this.add
          .text(400, 325, labels.empty, {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "18px",
            color: "#cbd5c0",
          })
          .setOrigin(0.5)
          .setDepth(5);

        this.rowItems.push(emptyText);
        updateScrollbar();
        return;
      }

      const maxStartIndex = Math.max(0, rows.length - this.maxVisibleRows);
      this.leaderboardStartIndex = Phaser.Math.Clamp(
        this.leaderboardStartIndex,
        0,
        maxStartIndex,
      );

      rows
        .slice(
          this.leaderboardStartIndex,
          this.leaderboardStartIndex + this.maxVisibleRows,
        )
        .forEach((row, visibleIndex) => {
          const index = this.leaderboardStartIndex + visibleIndex;
          const y = 210 + visibleIndex * 28;

          const background = this.add
            .rectangle(
              400,
              y,
              620,
              27,
              visibleIndex % 2 === 0 ? 0x203027 : 0x17231d,
              0.98,
            )
            .setStrokeStyle(1, 0x405747, 0.72)
            .setDepth(3);

          const rankText = this.add
            .text(122, y, `${index + 1}`, {
              fontFamily: '"Montserrat", sans-serif',
              fontSize: "15px",
              color: "#f7faf4",
              fontStyle: "bold",
            })
            .setOrigin(0.5)
            .setDepth(5);

          const rawIdentity =
            this.activeGroup === "adult"
              ? row.initials || "??"
              : row.participantId || "------";

          const identity =
            this.activeGroup === "minor"
              ? maskMinorParticipantId(rawIdentity)
              : rawIdentity;

          const isMinorEmail =
            this.activeGroup === "minor" && String(rawIdentity).includes("@");

          const identityText = this.add
            .text(183, y, identity, {
              fontFamily: isMinorEmail
                ? '"Montserrat", sans-serif'
                : this.activeGroup === "minor"
                  ? '"Courier New", monospace'
                  : '"Montserrat", sans-serif',
              fontSize: "16px",
              color: this.activeGroup === "minor" ? "#d9f99d" : "#f7faf4",
              fontStyle: "bold",
            })
            .setOrigin(0, 0.5)
            .setDepth(5);

          // Keep long masked emails inside the identity column so they
          // can never overlap the score column.
          const maxIdentityWidth = 370;
          if (identityText.width > maxIdentityWidth) {
            const fittedFontSize = Math.max(
              9,
              Math.floor(16 * (maxIdentityWidth / identityText.width)),
            );
            identityText.setFontSize(fittedFontSize);

            // Extremely long institutional domains are rare, but this
            // guarantees they still cannot cross into the score column.
            if (identityText.width > maxIdentityWidth) {
              identityText.setScale(maxIdentityWidth / identityText.width, 1);
            }
          }

          const scoreText = this.add
            .text(645, y, `${row.finalScore ?? 0}`, {
              fontFamily: '"Montserrat", sans-serif',
              fontSize: "16px",
              color: "#facc15",
              fontStyle: "bold",
            })
            .setOrigin(0.5)
            .setDepth(5);

          this.rowItems.push(background, rankText, identityText, scoreText);
        });

      if (rows.length > this.maxVisibleRows) {
        const scrollHint = this.add
          .text(400, 485, labels.scroll, {
            fontFamily: '"Montserrat", sans-serif',
            fontSize: "12px",
            color: "#aebba9",
          })
          .setOrigin(0.5)
          .setDepth(5);

        this.rowItems.push(scrollHint);
      }

      updateScrollbar();
    };

    this.renderLeaderboard = (event) => {
      if (this.loadingText) {
        this.loadingText.destroy();
        this.loadingText = null;
      }

      this.leaderboardRows = event.detail?.rows ?? [];
      this.leaderboardStartIndex = 0;
      this.renderRows();
    };

    this.scrollLeaderboard = (direction) => {
      const rows = getActiveRows();
      if (rows.length <= this.maxVisibleRows) return;

      const maxStartIndex = Math.max(0, rows.length - this.maxVisibleRows);
      const nextIndex = Phaser.Math.Clamp(
        this.leaderboardStartIndex + direction,
        0,
        maxStartIndex,
      );

      if (nextIndex === this.leaderboardStartIndex) return;
      this.leaderboardStartIndex = nextIndex;
      this.renderRows();
    };

    this.input.keyboard.on("keydown-UP", () => this.scrollLeaderboard(-1));
    this.input.keyboard.on("keydown-DOWN", () => this.scrollLeaderboard(1));
    this.input.on("wheel", (pointer, gameObjects, deltaX, deltaY) => {
      this.scrollLeaderboard(deltaY > 0 ? 1 : -1);
    });

    this.scrollbarTrack.on("pointerdown", (pointer) => {
      this.leaderboardSwipeStartY = null;
      setScrollFromThumbY(pointer.y);
    });

    this.scrollbarThumb.on("pointerdown", () => {
      this.isDraggingLeaderboardScrollbar = true;
      this.leaderboardSwipeStartY = null;
    });

    this.scrollbarThumb.on("drag", (pointer, dragX, dragY) => {
      setScrollFromThumbY(dragY);
    });

    this.scrollbarThumb.on("dragend", () => {
      this.isDraggingLeaderboardScrollbar = false;
      updateScrollbar();
    });

    // Mobile / tablet swipe scrolling.
    this.leaderboardSwipeStartY = null;

    this.input.on("pointerdown", (pointer) => {
      if (this.isDraggingLeaderboardScrollbar) return;
      this.leaderboardSwipeStartY = pointer.y;
    });

    this.input.on("pointerup", (pointer) => {
      if (this.isDraggingLeaderboardScrollbar) {
        this.leaderboardSwipeStartY = null;
        return;
      }
      if (this.leaderboardSwipeStartY === null) return;

      const deltaY = pointer.y - this.leaderboardSwipeStartY;
      this.leaderboardSwipeStartY = null;

      if (Math.abs(deltaY) < 30) return;

      this.scrollLeaderboard(deltaY < 0 ? 1 : -1);
    });

    window.addEventListener("biorev-leaderboard-ready", this.renderLeaderboard);
    window.dispatchEvent(new Event("biorev-request-leaderboard"));

    this.events.once("shutdown", () => {
      window.removeEventListener(
        "biorev-leaderboard-ready",
        this.renderLeaderboard,
      );
      this.input.keyboard.removeAllListeners("keydown-UP");
      this.input.keyboard.removeAllListeners("keydown-DOWN");
      this.input.removeAllListeners("wheel");
      this.input.removeAllListeners("pointerdown");
      this.input.removeAllListeners("pointerup");
    });

    const backButton = this.add
      .rectangle(400, 515, 200, 34, 0x0b1510, 0.98)
      .setStrokeStyle(2, 0x6f8f5b)
      .setDepth(3)
      .setInteractive({ useHandCursor: true });

    const backText = this.add
      .text(400, 515, t.return, {
        fontFamily: '"Montserrat", sans-serif',
        fontSize: "16px",
        color: "#f8fafc",
        fontStyle: "bold",
      })
      .setOrigin(0.5)
      .setDepth(4);

    backButton.on("pointerover", () => {
      this.sound.play("tap", { volume: 0.45 });
      backButton.setFillStyle(0x26382b, 1);
      backText.setColor("#d9f99d");
    });

    backButton.on("pointerout", () => {
      backButton.setFillStyle(0x0b1510, 0.98);
      backText.setColor("#f8fafc");
    });

    backButton.on("pointerdown", () => {
      this.sound.play("selected", { volume: 0.5 });
      this.scene.stop("LeaderboardScene");
      this.scene.resume("TitleScene");
    });
  }
}
